import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Drawer,
  CircularProgress,
  Menu,
  MenuItem,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";

import CropForm from "../components/CropForm";
import EventTypeForm from "../components/EventTypeForm";
import SoilTypeForm from "../components/SoilTypeForm";
import CropFamilyForm from "../components/CropFamilyForm";
import CropTypeForm from "../components/CropTypeForm";
import PlotTypeForm from "../components/PlotTypeForm";
import RotationFamilyRulesManager from "../components/RotationFamilyRulesManager";
import InviteUsers from "./InviteUsers";
import apiClient from "../utils/apiClient";
import AuthContext from "../auth/AuthContext";
import { useContext } from "react";


const ManagementPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState(0); // 0 = Event Types, 1 = Soil Types, 2 = Crops, 3 = Crop Families, 4 = Crop Types, 5 = Plot Types, 6 = User Invitations
  const [data, setData] = useState({
    eventTypes: [],
    soilTypes: [],
    crops: [],
    cropFamilies: [],
    cropTypes: [],
    plotTypes: [],
  });
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formType, setFormType] = useState(""); // "eventType", "soilType", "crop", "cropFamily", "cropType", "plotType"
  const [editData, setEditData] = useState(null); // Data for editing
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [eventTypeRes, soilTypeRes, cropRes, cropFamilyRes, cropTypeRes, plotTypeRes] = await Promise.all([
          apiClient.get("/eventtypes"),
          apiClient.get("/soiltypes"),
          apiClient.get("/crops"),
          apiClient.get("/cropfamilies"),
          apiClient.get("/croptypes"),
          apiClient.get("/plottypes"),
        ]);
        setData({
          eventTypes: eventTypeRes.data,
          soilTypes: soilTypeRes.data,
          crops: cropRes.data,
          cropFamilies: cropFamilyRes.data,
          cropTypes: cropTypeRes.data,
          plotTypes: plotTypeRes.data,
        });
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddClick = (type) => {
    setFormType(type);
    setEditData(null); // Clear edit data for new entry
    setIsDrawerOpen(true);
  };

  const handleEditClick = (type, item) => {
    setFormType(type);
    setEditData(item);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setFormType("");
    setEditData(null);
  };

  const handleFormSubmit = async (newItem) => {
    try {
      let endpoint, key, uniqueId;
      if (formType === "eventType") {
        endpoint = editData ? `/eventtypes/${editData.event_type_uuid}` : "/eventtypes";
        key = "eventTypes";
        uniqueId = "event_type_uuid";
      } else if (formType === "soilType") {
        endpoint = editData ? `/soiltypes/${editData.soil_type_uuid}` : "/soiltypes";
        key = "soilTypes";
        uniqueId = "soil_type_uuid";
      } else if (formType === "crop") {
        endpoint = editData ? `/crops/${editData.crop_uuid}` : "/crops";
        key = "crops";
        uniqueId = "crop_uuid";
      } else if (formType === "cropFamily") {
        endpoint = editData ? `/cropfamilies/${editData.crop_family_uuid}` : "/cropfamilies";
        key = "cropFamilies";
        uniqueId = "crop_family_uuid";
      } else if (formType === "cropType") {
        endpoint = editData ? `/croptypes/${editData.crop_type_uuid}` : "/croptypes";
        key = "cropTypes";
        uniqueId = "crop_type_uuid";
      } else if (formType === "plotType") {
        endpoint = editData ? `/plottypes/${editData.plot_type_uuid}` : "/plottypes";
        key = "plotTypes";
        uniqueId = "plot_type_uuid";
      }

      const method = editData ? "put" : "post";
      const response = await apiClient[method](endpoint, newItem);

      if (formType === "crop") {
        // Refetch crops so data includes crop_windows (PUT response may not have them)
        const cropRes = await apiClient.get("/crops");
        setData((prev) => ({ ...prev, crops: cropRes.data }));
      } else {
        setData((prev) => ({
          ...prev,
          [key]: editData
            ? prev[key].map((item) => (item[uniqueId] === editData[uniqueId] ? response.data : item))
            : [...prev[key], response.data],
        }));
      }
    } catch (err) {
      console.error("Error adding/updating item:", err);
    } finally {
      handleDrawerClose();
    }
  };

  const handleDelete = async (id) => {
    try {
      let endpoint, key;
      if (formType === "eventType") {
        endpoint = `/eventtypes/${id}`;
        key = "eventTypes";
      } else if (formType === "soilType") {
        endpoint = `/soiltypes/${id}`;
        key = "soilTypes";
      } else if (formType === "crop") {
        endpoint = `/crops/${id}`;
        key = "crops";
      } else if (formType === "cropFamily") {
        endpoint = `/cropfamilies/${id}`;
        key = "cropFamilies";
      } else if (formType === "cropType") {
        endpoint = `/croptypes/${id}`;
        key = "cropTypes";
      } else if (formType === "plotType") {
        endpoint = `/plottypes/${id}`;
        key = "plotTypes";
      }

      await apiClient.delete(endpoint);

      setData((prev) => ({
        ...prev,
        [key]: prev[key].filter((item) => {
          const uniqueId = formType === "eventType" ? "event_type_uuid" :
                          formType === "soilType" ? "soil_type_uuid" :
                          formType === "crop" ? "crop_uuid" :
                          formType === "cropFamily" ? "crop_family_uuid" :
                          formType === "cropType" ? "crop_type_uuid" :
                          formType === "plotType" ? "plot_type_uuid" : "";
          return item[uniqueId] !== id;
        }),
      }));
    } catch (err) {
      console.error("Error deleting item:", err);
      alert(`Error deleting ${formType}: ${err.message || 'Unknown error'}`);
    } finally {
      handleDrawerClose();
    }
  };

  const views = [
    {
      label: "Event Types",
      dataKey: "eventTypes",
      columns: ["Name", "Description"],
    },
    {
      label: "Soil Types",
      dataKey: "soilTypes",
      columns: ["Name", "Description"],
    },
    {
      label: "Crops",
      dataKey: "crops",
      columns: ["Name", "Description", "Type"],
    },
    {
      label: "Crop Families",
      dataKey: "cropFamilies",
      columns: ["Name", "Description"],
    },
    {
      label: "Crop Types",
      dataKey: "cropTypes",
      columns: ["Name", "Description"],
    },
    {
      label: "Plot Types",
      dataKey: "plotTypes",
      columns: ["Name", "Description", "Default Shape", "Form Fields"],
    },
    {
      label: "Rotation Family Rules",
      dataKey: "rotationFamilyRules",
      columns: [],
      isSpecial: true,
      component: RotationFamilyRulesManager,
    },
    ...(user?.is_admin ? [{
      label: "User Invitations",
      dataKey: "invitations",
      columns: [],
      isSpecial: true
    }] : []),
  ];

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSecretSantaClick = () => {
    handleMenuClose();
    navigate("/christmas");
  };

  return (
    <Box className="page-container">
      <Typography variant="h4" gutterBottom>
        General Management
      </Typography>

      <Tabs
        className="management-tabs"
        value={currentView}
        onChange={(e, newValue) => {
          // Check if it's the menu tab (last tab)
          if (newValue === views.length) {
            handleMenuOpen(e);
            // Don't change currentView, keep it as is
            return;
          }
          setCurrentView(newValue);
        }}
        sx={{ mb: 2 }}
      >
        {views.map((view, index) => (
          <Tab key={index} label={view.label} />
        ))}
        <Tab
          icon={<MenuIcon />}
          iconPosition="start"
          label="Menu"
          value={views.length}
        />
      </Tabs>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleSecretSantaClick}>Secret Santa</MenuItem>
      </Menu>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box>
          {views[currentView].isSpecial ? (
            views[currentView].component === RotationFamilyRulesManager ? (
              <RotationFamilyRulesManager />
            ) : (
              <InviteUsers />
            )
          ) : (
            <>
              <TableContainer component={Paper} className="table-container">
                <Table>
                  <TableHead>
                    <TableRow>
                      {views[currentView].columns.map((col) => (
                        <TableCell key={col}>{col}</TableCell>
                      ))}
                      <TableCell>Edit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data[views[currentView].dataKey].map((item) => (
                      <TableRow key={item.id || item.uuid || item.event_type_uuid || item.crop_family_uuid}>
                        {views[currentView].columns.map((col) => {
                          const colKey = col.toLowerCase().replace(" ", "_");
                          let value;
                          
                          // Special handling for "Type" column in crops table
                          if (col === "Type" && views[currentView].dataKey === "crops") {
                            value = item.crop_type?.name || "N/A";
                          } else {
                            // Find matching key in item
                            const matchingKey = Object.keys(item).find(
                              (key) => key === colKey || key === colKey.replace("_", "")
                            );
                            value = matchingKey ? item[matchingKey] : null;
                            
                            // Handle arrays and objects
                            if (Array.isArray(value)) {
                              value = value.map((v) => v.name || v).join(", ");
                            } else if (value && typeof value === "object") {
                              value = value.name || JSON.stringify(value);
                            }
                          }
                          
                          return (
                            <TableCell key={col}>
                              {value || "N/A"}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <IconButton
                            onClick={() =>
                              handleEditClick(
                                currentView === 0
                                  ? "eventType"
                                  : currentView === 1
                                  ? "soilType"
                                  : currentView === 2
                                  ? "crop"
                                  : currentView === 3
                                  ? "cropFamily"
                                  : currentView === 4
                                  ? "cropType"
                                  : "plotType",
                                item
                              )
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <IconButton
                className="add-button"
                onClick={() =>
                  handleAddClick(
                    currentView === 0
                      ? "eventType"
                      : currentView === 1
                      ? "soilType"
                      : currentView === 2
                      ? "crop"
                      : currentView === 3
                      ? "cropFamily"
                      : currentView === 4
                      ? "cropType"
                      : "plotType"
                  )
                }
              >
                <AddIcon />
              </IconButton>
            </>
          )}
        </Box>
      )}

      <Drawer anchor="right" open={isDrawerOpen} onClose={handleDrawerClose}>
        <Box className="drawer-content">
          {formType === "eventType" && (
            <EventTypeForm
              onSubmit={handleFormSubmit}
              onCancel={handleDrawerClose}
              initialData={editData}
            />
          )}
          {formType === "soilType" && (
            <SoilTypeForm
              onSubmit={handleFormSubmit}
              onCancel={handleDrawerClose}
              initialData={editData}
            />
          )}
          {formType === "crop" && (
            <CropForm
              onSubmit={handleFormSubmit}
              onCancel={handleDrawerClose}
              initialData={editData}
            />
          )}
          {formType === "cropFamily" && (
            <CropFamilyForm
              onSubmit={handleFormSubmit}
              onCancel={handleDrawerClose}
              initialData={editData}
            />
          )}
          {formType === "cropType" && (
            <CropTypeForm
              onSubmit={handleFormSubmit}
              onCancel={handleDrawerClose}
              onDelete={handleDelete}
              initialData={editData}
            />
          )}
          {formType === "plotType" && (
            <PlotTypeForm
              onSubmit={handleFormSubmit}
              onCancel={handleDrawerClose}
              initialData={editData}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default ManagementPage;
